import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Wrench, Zap, FileText, Newspaper } from "lucide-react";
import { Seo } from "@/components/Seo";

// Cada categoría del changelog tiene una identidad visual propia para que el
// usuario reconozca de un vistazo qué tipo de cambio fue. Los colores se
// alinean con el resto de la app (rojo=cartas, morado=sellados, ámbar=avisos).
const CATEGORY_META = {
  new: {
    label: "Nuevo",
    Icon: Sparkles,
    pill: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    stripe: "border-l-emerald-500",
  },
  fix: {
    label: "Arreglo",
    Icon: Wrench,
    pill: "bg-sky-500/15 text-sky-300 border-sky-500/40",
    stripe: "border-l-sky-500",
  },
  improvement: {
    label: "Mejora",
    Icon: Zap,
    pill: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    stripe: "border-l-amber-500",
  },
  legal: {
    label: "Legal",
    Icon: FileText,
    pill: "bg-purple-500/15 text-purple-300 border-purple-500/40",
    stripe: "border-l-purple-500",
  },
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-EC", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function Changelog() {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");

  const load = async () => {
    setError(null);
    try {
      const r = await api.get("/changelog");
      setEntries(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudieron cargar las novedades.");
      setEntries([]);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = entries === null
    ? []
    : activeFilter === "all"
      ? entries
      : entries.filter((e) => e.category === activeFilter);

  const filterBtn = (key, label) => (
    <button
      key={key}
      type="button"
      data-testid={`changelog-filter-${key}`}
      onClick={() => setActiveFilter(key)}
      className={`px-3 py-1.5 text-xs sm:text-sm rounded-md border transition-colors ${activeFilter === key ? "bg-white text-black border-white" : "bg-[#111] text-neutral-400 border-[#262626] hover:border-neutral-600 hover:text-white"}`}
    >
      {label}
    </button>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="changelog-page">
      <Seo
        title="Novedades — Cambios, mejoras y correcciones"
        description="Historial público de cambios, arreglos y mejoras de HIT TCG, el marketplace ecuatoriano de cartas Pokémon y Riftbound."
        keywords="novedades HitTCG, changelog cartas Pokémon Ecuador, actualizaciones marketplace TCG"
        url="/novedades"
      />
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-md bg-[#EF4444]/10 border border-[#EF4444]/30 flex items-center justify-center">
          <Newspaper size={20} className="text-[#EF4444]" />
        </div>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl leading-tight">Novedades</h1>
          <p className="text-xs sm:text-sm text-neutral-500 mt-0.5">Cambios, mejoras y correcciones de HitTCG</p>
        </div>
      </div>

      <div className="mt-6 mb-8 flex items-center gap-2 flex-wrap" data-testid="changelog-filters">
        {filterBtn("all", "Todas")}
        {filterBtn("new", "🆕 Nuevo")}
        {filterBtn("fix", "🔧 Arreglos")}
        {filterBtn("improvement", "⚡ Mejoras")}
        {filterBtn("legal", "📄 Legal")}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/40 rounded-md p-4 text-sm text-red-200" data-testid="changelog-error">
          {error}
        </div>
      )}

      {entries === null && !error ? (
        <ol className="space-y-4" data-testid="changelog-skeleton">
          {[1, 2, 3].map((i) => (
            <li key={i} className="bg-[#111] border border-[#262626] rounded-md p-4 animate-pulse">
              <div className="h-3 w-24 bg-[#1f1f1f] rounded mb-3" />
              <div className="h-4 w-2/3 bg-[#1f1f1f] rounded mb-2" />
              <div className="h-3 w-full bg-[#1f1f1f] rounded" />
            </li>
          ))}
        </ol>
      ) : filtered.length === 0 ? (
        <div className="bg-[#111] border border-[#262626] rounded-md p-8 text-center text-neutral-500 text-sm" data-testid="changelog-empty">
          {entries && entries.length === 0
            ? "Aún no hemos publicado novedades. Vuelve pronto."
            : "No hay entradas en esta categoría."}
        </div>
      ) : (
        <ol className="relative space-y-4" data-testid="changelog-list">
          {filtered.map((e) => {
            const meta = CATEGORY_META[e.category] || CATEGORY_META.improvement;
            const Icon = meta.Icon;
            return (
              <li
                key={e.id}
                data-testid={`changelog-entry-${e.id}`}
                className={`bg-[#111] border border-[#262626] rounded-md p-4 sm:p-5 border-l-4 ${meta.stripe}`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                  <span
                    className={`inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] px-2 py-1 rounded border uppercase tracking-wider ${meta.pill}`}
                    data-testid={`changelog-entry-category-${e.id}`}
                  >
                    <Icon size={12} /> {meta.label}
                  </span>
                  <time
                    dateTime={e.published_at}
                    className="text-[10px] sm:text-xs text-neutral-500 font-mono"
                    data-testid={`changelog-entry-date-${e.id}`}
                  >
                    {formatDateTime(e.published_at)}
                  </time>
                </div>
                <h2 className="font-display text-base sm:text-lg text-white leading-snug">{e.title}</h2>
                <p className="text-sm text-neutral-400 mt-1.5 whitespace-pre-line leading-relaxed">{e.description}</p>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
